import { Formik } from 'components/form/formik';
import * as Yup from 'yup';
import { PlusIcon } from '@phosphor-icons/react';

import { Button } from '../../../components/button/Button';
import { CheckboxField } from '../../../components/checkbox/CheckboxField';
import { InputField } from '../../../components/input/InputField';
import { SimpleSelectField } from '../../../components/select/SimpleSelectField';
import { TextAreaField } from '../../../components/textarea/TextAreaField';
import { Country, Language } from 'src/types/enums';
import { COUNTRY_VALUES } from '../../../utils/address';
import { LANGUAGE_VALUES } from '../../../utils/language';
import { AutocompletePostalcode } from './AutocompletePostalcode';
import { Card } from 'components/Card';
import type { CreateLocationInput } from '../models';

const createLocationSchema = Yup.object().shape({
  name: Yup.string().min(1, 'Vereist').required('Vereist'),
  street: Yup.string().min(1, 'Vereist').required('Vereist'),
  streetNumber: Yup.string(),
  city: Yup.string().min(1, 'Vereist').required('Vereist'),
  postalCode: Yup.string().min(1, 'Vereist').required('Vereist'),
  country: Yup.mixed().nullable().required('Vereist'),
  language: Yup.mixed().nullable().required('Vereist'),
});

export interface ICreateLocationFormProps {
  /** When passed, a cancel button is rendered next to the submit button. */
  onCancel?: () => void;
  /** Must never throw — the caller owns error handling and reporting. */
  onSubmit: (input: CreateLocationInput) => Promise<void>;
  /** Prefills the name field, e.g. with what the user typed in a combobox. */
  initialName?: string;
}

export const CreateLocationForm: React.FC<ICreateLocationFormProps> = (props) => {
  const { onCancel, onSubmit, initialName = '' } = props;

  return (
    <Formik
      initialValues={{
        name: initialName,
        street: '',
        streetNumber: '',
        city: '',
        postalCode: '',
        country: COUNTRY_VALUES.find((v) => v.key === Country.Be)!,
        language: LANGUAGE_VALUES.find((v) => v.key === Language.Nl)!,
        internalNotes: '',
        externalNotes: '',
        isExternalBooking: false,
      }}
      validationSchema={createLocationSchema}
      onSubmit={async (values) => {
        await onSubmit({
          ...values,
          country: values.country.key as Country,
          language: values.language.key as Language,
        });
      }}
    >
      {({ handleSubmit, isSubmitting }) => (
        <form
          onSubmit={(evt) => {
            // This form can render inside a dialog that is portaled to <body>, but React
            // propagates `submit` over the fiber tree — without this, the page's own form
            // (e.g. the order form) would submit too.
            evt.stopPropagation();
            handleSubmit(evt);
          }}
          className="grid gap-4"
        >
          <Card title="Algemene informatie">
            <InputField labelText="Naam" type="text" name="name" />
            <div className="flex gap-4">
              <div className="flex-4">
                <InputField labelText="Straat" type="text" name="street" />
              </div>
              <div className="flex-1">
                <InputField labelText="Nr" type="text" name="streetNumber" />
              </div>
            </div>

            <AutocompletePostalcode />

            <SimpleSelectField labelText="Taal" items={LANGUAGE_VALUES} name="language" />

            <CheckboxField labelText="Externe boeking" name="isExternalBooking" />
          </Card>

          <Card title="Notities">
            <TextAreaField labelText="Interne notities" name="internalNotes" spellCheck={true} />
            <TextAreaField labelText="Externe notities (voor vervoerder)" name="externalNotes" spellCheck={true} />
          </Card>

          <div className="flex justify-between">
            {onCancel && (
              <Button type="button" color="secondary" isDisabled={isSubmitting} onTrigger={onCancel}>
                Annuleer
              </Button>
            )}

            <Button
              type="submit"
              color="primary"
              iconLeft={<PlusIcon className="button-icon" />}
              isDisabled={isSubmitting}
              isLoading={isSubmitting}
            >
              Maak locatie aan
            </Button>
          </div>
        </form>
      )}
    </Formik>
  );
};
